import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { 
  LayoutDashboard, 
  Plus, 
  Package, 
  LogOut,
  Menu,
  X,
  Wrench,
  Users,
  PackageOpen,
  ShieldCheck,
  Gavel,
  Tag,
  Camera,
  QrCode,
  ShoppingCart,
  CalendarDays,
  Percent,
  Globe
} from "lucide-react";
import { useState } from "react";

export const Layout = ({ children }) => {
  const { user, logout, isAdmin, isMechanic } = useAuth();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Role-based navigation
  const isMechanicUser = user?.role === "mechanic";

  const navItems = isMechanicUser ? [
    { path: "/mechanic-dashboard", icon: LayoutDashboard, label: "Panou" },
    { path: "/mechanic-parts", icon: ShoppingCart, label: "Comenzi Piese" },
  ] : [
    { path: "/dashboard", icon: LayoutDashboard, label: "Panou" },
    { path: "/reception", icon: PackageOpen, label: "Receptie" },
    { path: "/stock", icon: Package, label: "Stoc" },
    { path: "/parts", icon: Wrench, label: "Piese" },
    { path: "/labels", icon: Tag, label: "Etichete" },
    { path: "/photographer", icon: Camera, label: "Fotografii" },
    { path: "/scan", icon: QrCode, label: "Scanner QR" },
    { path: "/register-scooter", icon: Plus, label: "Inregistrare" },
    { path: "/reports", icon: LayoutDashboard, label: "Rapoarte" },
    { path: "/mechanic-parts", icon: ShoppingCart, label: "Comenzi Piese" },
    { path: "/programari", icon: CalendarDays, label: "Programari" },
  ];
  
  // Add Clients and Admin nav items for admins
  if (!isMechanicUser && isAdmin && isAdmin()) {
    navItems.push({ path: "/clients", icon: Users, label: "Clients" });
    navItems.push({ path: "/reduceri", icon: Percent, label: "Reduceri" });
    navItems.push({ path: "/carte-qr", icon: QrCode, label: "Carte QR" });
    navItems.push({ path: "/eicma-leads", icon: Globe, label: "EICMA" });
    navItems.push({ path: "/admin/auctions", icon: Gavel, label: "Encheres" });
    navItems.push({ path: "/admin", icon: ShieldCheck, label: "Admin" });
  }

  const isActive = (path) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header */}
      <header className="glass-header sticky top-0 z-50 px-4 lg:px-8 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-3 flex-shrink-0">
            <img src="https://customer-assets.emergentagent.com/job_509254e3-4fbd-4a68-8194-b9dcfb6ff3e4/artifacts/bylh1er6_Image.jpg" alt="Scutersh" className="w-10 h-10 rounded-md object-contain" />
            <div className="hidden sm:block">
              <h1 className="font-heading font-bold text-lg leading-tight">SCUTERSH</h1>
              <p className="text-xs text-muted-foreground font-mono">Management</p>
            </div>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex flex-wrap justify-center items-center gap-1 flex-1 min-w-0 mx-4">
            {navItems.map((item) => (
              <Link key={item.path} to={item.path}>
                <Button
                  variant={isActive(item.path) ? "default" : "ghost"}
                  size="sm"
                  className={isActive(item.path) 
                    ? "bg-primary text-white font-bold uppercase text-xs tracking-wide" 
                    : "text-muted-foreground hover:text-foreground"
                  }
                  data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                >
                  <item.icon className="w-4 h-4 mr-2" />
                  {item.label}
                </Button>
              </Link>
            ))}
          </nav>

          {/* User Info & Logout */}
          <div className="flex items-center gap-4">
            <span className="hidden sm:block text-sm text-muted-foreground font-mono">
              {user?.username}
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={logout}
              className="text-muted-foreground hover:text-destructive"
              data-testid="btn-logout"
            >
              <LogOut className="w-4 h-4" />
            </Button>

            {/* Mobile Menu Toggle */}
            <Button
              variant="ghost"
              size="sm"
              className="md:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="btn-mobile-menu"
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
          </div>
        </div>

        {/* Mobile Nav */}
        {mobileMenuOpen && (
          <nav className="md:hidden mt-4 pb-2 flex flex-col gap-2 animate-fade-in">
            {navItems.map((item) => (
              <Link 
                key={item.path} 
                to={item.path}
                onClick={() => setMobileMenuOpen(false)}
              >
                <Button
                  variant={isActive(item.path) ? "default" : "ghost"}
                  className={`w-full justify-start ${
                    isActive(item.path) 
                      ? "bg-primary text-white font-bold" 
                      : "text-muted-foreground"
                  }`}
                >
                  <item.icon className="w-4 h-4 mr-2" />
                  {item.label}
                </Button>
              </Link>
            ))}
          </nav>
        )}
      </header>

      {/* Main Content */}
      <main className="flex-1 px-4 lg:px-8 py-8">
        <div className="max-w-7xl mx-auto animate-fade-in">
          {children}
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-border px-4 lg:px-8 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between text-xs text-muted-foreground">
          <span className="font-mono">© 2026 Scutersh</span>
          <span className="font-mono">Scutere & Piese Premium</span>
        </div>
      </footer>
    </div>
  );
};
